/**
 * Sampo — API-sopimus.
 *
 * Nämä tyypit vastaavat 003_kyselyt.sql:n funktioiden paluutyyppejä
 * sarake sarakkeelta. Jos SQL muuttuu, tämä tiedosto muuttuu samassa
 * commitissa — muuten rajapinta valehtelee.
 *
 * Sekä API että käyttöliittymä importtaavat tästä. Se on ainoa syy
 * miksi TypeScript on tässä projektissa: rajapinnan molemmat puolet
 * tarkistetaan samaa määritelmää vasten, eikä virhe pääse tuotantoon
 * odottamaan että joku huomaa sen.
 */

/* ── Moduulitunnukset ─────────────────────────────────────── */

/**
 * Sama lista kuin `api/src/moduulit.ts`. Kaksoiskappale on tahallinen:
 * `api/tsconfig.json` rajaa käännöksen `rootDir: "src"`:iin, joten API ei
 * voi importata tämän hakemiston ulkopuolelta ilman että dist-rakenne ja
 * PM2:n polut muuttuvat. Vaihtoehdot olivat kaksoiskappale tai jaettu
 * paketti; jälkimmäinen olisi kuudes riippuvuus ja oma julkaisukierto.
 *
 * Kaksoiskappale ei silti saa erkaantua ihmisen muistin varassa:
 * `npm run moduulitarkistus` vertaa nämä kaksi listaa toisiinsa ja
 * kaatuu jos ne eroavat. Sopimus on siis sama kummallakin puolella,
 * ja kone tarkistaa sen — ei ihminen.
 *
 * Syy tyypitykselle on sama kuin palvelimella: selainpuolen portti
 * erehtyy kiinni päin (`paalla()` main.tsx), ja fail-closed muuttaa
 * kirjoitusvirheen vikamuodon. Ennen se olisi näyttänyt liikaa; nyt se
 * piilottaisi navi-kohdan hiljaa, mikä on vaikeampi huomata. Tyyppi
 * tekee siitä käännösvirheen ennen kumpaakaan.
 */
/** Käyttöliittymän kielet (205, T439). Sama lista api/src/kieli.ts:ssä; moduulitarkistus vertaa. */
/** Hubin tarjouskortti: vain nämä roolit rajaamattomana. Kopio api/src/hubsaannot.ts:stä; hubsaannot.test.ts vertaa. */
export const TARJOUSKORTIN_ROOLIT = ["paakayttaja", "suunnittelija"] as const;

export const KAYTTOLIITTYMAN_KIELET = ["fi", "en", "lt", "et"] as const;
export type Kieli = typeof KAYTTOLIITTYMAN_KIELET[number];

export const MODUULIT = [
  "resurssit",
  "kohteet",
  "mitoitus",
  "henkilot",
  "patevyydet",
  "poissaolot",
  "liitteet",
  "kayttajat",
  "puutteet",
  "hitsausloki",
  "kalusto",
  "tarjous",
  "osaaminen",
  "laiteasennus",
  "iv_asennus",
  "suunnittelu",
] as const;

export type ModuuliTunnus = typeof MODUULIT[number];

/* ── Perustyypit ──────────────────────────────────────────── */

/** ISO-päivämäärä, esim. "2026-08-24" */
export type Pvm = string;

/**
 * Neljä arvoa, koska `henkilo`n CHECK-rajoite sallii neljä. Nelja on
 * `ulkopuolinen`, joka on tiedossa oleva inertti jäänne (ks. CLAUDE.md)
 * — rivejä ei ole, mutta tyyppi joka kieltää kannan sallimasta arvosta
 * kaatuu vasta kun rivi ilmestyy.
 *
 * Tämä oli 29.8.2026 asti määritelty KAHDESTI eri sisällöllä
 * (`api.ts` neljä, tämä kolme). Kumpikin oli oikeassa jostain, eikä
 * mikään kaatunut erosta.
 */
export type Tyosuhde = "oma" | "vuokra" | "alihankinta" | "ulkopuolinen";

/** GET /api/kapasiteetti/rekisteri — laskennan lahtojoukko nakyvana. */
export interface KapasiteettiRekisteri {
  henkiloita: number;
  paattyneita: number;
  tyoaikamalli: number;
  /** Tehollinen viikkomaksimi = punaisen raja. NULL jos ei henkiloita. */
  raja_min: number | null;
  raja_max: number | null;
  /** Montako ajaa migraation 001 koskematonta oletusta 10 h/pv. */
  oletusmaksimi: number;
  /** Montako laskennassa on lauantai työpäivänä. */
  lauantaihenkiloita: number;
  /** Millä tuntimäärällä lauantai lasketaan (= arkipäivän pituus). */
  lauantain_tunnit: number | null;
}

export type AllokaationTila = "ehdotus" | "vahvistettu";

export type PoissaolonTila = "haettu" | "hyvaksytty" | "hylatty";

export type KohteenLaji = "projekti" | "jatkuva";

/* ── f_kapasiteetti_paiva(alku, loppu) ────────────────────── */

export interface KapasiteettiPaiva {
  henkilo_id: number;
  lyhenne: string | null;
  nimi: string;
  tyosuhde: Tyosuhde;
  nimeamaton: boolean;
  pvm: Pvm;
  tyopaiva: boolean;
  poissa: boolean;
  /** Poissaololajin tunnus. Arkaluonteinen laji suodatetaan API:ssa roolin mukaan. */
  poissaolo_laji: string | null;
  /** Työtä päivänä, joka ei ole henkilön työpäivä. Poikkeus, ei virhe. */
  poikkeuspaiva: boolean;
  nimellinen: number;
  maksimi: number;
  /** Viikkokatto tunteina, null jos kattoa ei ole. */
  maksimi_viikko: number | null;
  kaytettavissa: number;
  vahvistettu: number;
  ehdotus: number;
  /** allokoitu / maksimi. null jos maksimi on 0. */
  kuormitus: number | null;
  ylitys: boolean;
  /** Allokaatio poissaolon tai vapaapäivän päälle. */
  virhe_poissa: boolean;
}

/* ── f_kapasiteetti_viikko(alku, loppu) ───────────────────── */

export interface KapasiteettiViikko {
  henkilo_id: number;
  lyhenne: string | null;
  nimi: string;
  tyosuhde: Tyosuhde;
  nimeamaton: boolean;
  viikko_alku: Pvm;
  viikko: number;
  tyopaivia: number;
  poissa_paivia: number;
  /** Viikonloppu- tai pyhätyöpäiviä. */
  poikkeuspaivia: number;
  kaytettavissa: number;
  maksimi: number;
  allokoitu: number;
  vapaa: number;
  kuormitus: number | null;
  ylitys: boolean;
  virhe_poissa: boolean;
}

/* ── f_vaje(alku, loppu) ──────────────────────────────────── */

/**
 * Ketju: tarve → allokoitu → kattamaton → vapaa → alihankintatarve → vaje.
 *
 * Ero alihankintatarpeen ja vajeen välillä on liiketoiminnallinen:
 * alihankintatarve tarkoittaa että soitetaan vuokrafirmalle,
 * vaje tarkoittaa ettei urakkaa voida ottaa nykyisellä porukalla.
 */
export interface Vaje {
  osaamisluokka_id: number;
  osaamisluokka: string;
  viikko_alku: Pvm;
  viikko: number;
  /** Mitä työkohteet ilmoittavat tarvitsevansa, tunteina. */
  tarve: number;
  allokoitu: number;
  /** tarve − allokoitu */
  kattamaton: number;
  vapaa_oma: number;
  /** Täyttämättömien vuokrapaikkojen kapasiteetti. */
  vapaa_vuokra: number;
  /** Mitä oma porukka ei kata → ostettava ulkoa. */
  alihankintatarve: number;
  /** Mitä ei kateta edes vuokraamalla → rekrytointi tai urakan siirto. */
  vaje: number;
  henkiloita_omia: number;
  vuokrapaikkoja: number;
}

/* ── f_varamiehet(alku, loppu) ────────────────────────────── */

export type Riski =
  | "kriittinen"
  | "ei varamiestä, vuokravaraa on"
  | "yhden varassa"
  | "käyttöaste korkea"
  | "ok";

export interface Varamiehet {
  osaamisluokka: string;
  viikko_alku: Pvm;
  viikko: number;
  luokan_koko: number;
  /** Oman henkilöstön jäsenet, joiden kuormitus on alle 40 %. */
  varamiehia: number;
  vuokravaraa: number;
  /** Alle konfiguroidun rajan (oletus 8) → prosenttipuskuri on merkityksetön. */
  pieni_luokka: boolean;
  kayttoaste: number | null;
  riski: Riski;
}

/* ── v_patevyys_tila ──────────────────────────────────────── */

export type PatevyydenTila =
  | "voimassa"
  | "varoitus"      // ≤ 90 vrk
  | "kriittinen"    // ≤ 30 vrk
  | "vanhentunut"
  | "ei vanhene";

export interface PatevyysTila {
  henkilo_id: number;
  lyhenne: string | null;
  nimi: string;
  tyosuhde: Tyosuhde;
  patevyys: string;
  voimassa_asti: Pvm | null;
  paivia_jaljella: number | null;
  varmennettu: boolean;
  epavarma: boolean;
  tila: PatevyydenTila;
}

/* ── f_patevyys_riski(alku, loppu) ────────────────────────── */

/** Pätevyys umpeutuu kesken allokoidun työn. Kalenterinäkymä ei näytä tätä. */
export interface PatevyysRiski {
  henkilo_id: number;
  lyhenne: string | null;
  nimi: string;
  patevyys: string;
  voimassa_asti: Pvm;
  kohde: string;
  allokaatio_asti: Pvm;
  tunteja_jalkeen: number;
}

/* ── f_kohteet_paallekkain(alku, loppu) ───────────────────── */

export interface KohdePaallekkaisyys {
  kohde_id: number;
  tunnus: string;
  nimi: string;
  laji: KohteenLaji;
  /** Elinkaari. Aikajana merkitsee kohteen joka ei varaa kapasiteettia
   *  — se ei saa kadota, mutta ei saa näyttää käynnissä olevaltakaan. */
  tila: string;
  alkaa: Pvm | null;
  paattyy: Pvm | null;
  kesto_tyopaivia: number;
  henkiloita: number;
  tunteja: number;
  paallekkaisia: number;
}

/* ── Kirjoitusrajapinta ───────────────────────────────────── */

/** Allokaatio tallennetaan tunteina päivää kohti. Muut yksiköt johdetaan. */
export interface AllokaatioSyote {
  resurssi_id: number;
  kohde_id: number;
  pvm: Pvm;
  tunnit: number;
  laji: "tyo" | "varallaolo" | "matka";
  tila: AllokaationTila;
  huomiot?: string;
}

/** Massasyöttö: sama allokaatio jakson jokaiselle työpäivälle. */
export interface AllokaatioJakso {
  resurssi_id: number;
  kohde_id: number;
  alkaa: Pvm;
  paattyy: Pvm;
  tunnit_per_paiva: number;
  laji: "tyo" | "varallaolo" | "matka";
  tila: AllokaationTila;
}

export interface PoissaoloSyote {
  henkilo_id: number;
  poissaololaji_id: number;
  alkaa: Pvm;
  /** Puoliavoin väli: loppupäivä ei sisälly. */
  paattyy: Pvm;
  huomiot?: string;
}

/* ── Brändi ───────────────────────────────────────────────── */

/**
 * Luetaan konfiguraatiosta kerran käynnistyksessä ja asetetaan
 * CSS-muuttujiksi. Yksikään väri ei ole koodissa kovana.
 *
 * brandi.* saa muuttua asiakkaan mukaan.
 * tila.*  on tuotteen kieltä — muuta vain pakosta.
 */
export interface Brandi {
  nimi: string;
  tuotenimi: string;
  logo: string;
  logo_tumma: string | null;
  favicon: string;
  paavari: string;
  paavari_teksti: string;
  korostus: string;
  ylapalkki_tumma: boolean;
  raportti_ylatunniste: string;
  raportti_alatunniste: string;
  kieli: "fi" | "en";
  aikavyohyke: string;
}

export interface TilaVarit {
  kuorma: string;
  kuorma_kevyt: string;
  kuorma_kevein: string;
  ylitys: string;
  poissa: string;
  ristiriita: string;
  varamies: string;
  vuokra: string;
}

/* ── Virheet ──────────────────────────────────────────────── */

/**
 * API palauttaa virheet aina tässä muodossa. Viesti on käyttäjälle
 * näytettävä ja kirjoitettu käyttöliittymän äänellä: kerrotaan mitä
 * tapahtui ja miten se korjataan, ei pyydetä anteeksi.
 */
export interface ApiVirhe {
  virhe: string;
  viesti: string;
  kentta?: string;
}
